/**
 * Wally estimated delivery on the product page. Markup and notes:
 * snippets/wally-delivery-estimate.liquid.
 *
 *   Usually arrives Tue 27 to Thu 29 Oct
 *   Order in the next 2 hrs 14 mins for same-day dispatch
 *
 * Everything runs on New Zealand time, whatever the shopper's clock says. We dispatch on NZ
 * business days (Mon to Fri, not a public holiday), the same day if the order is in before
 * 3pm. Transit counts working days: NZ ones for NZ delivery, plain Mon to Fri elsewhere (other
 * countries' holidays aren't tracked; the ranges are wide and the line says "usually").
 *
 * The row hides for a size that doesn't ship now. Horizon re-renders the product section on a
 * size change and fires variant:update with the new HTML, the same event assets/wally-preorder.js
 * syncs from. A size showing a pre-order, special order or back in stock note (they all share
 * .wally-preorder-note) gets no delivery date.
 */
(function () {
  'use strict';

  var TZ = 'Pacific/Auckland';
  var CUTOFF = 15 * 60; // 3pm NZ, in minutes after midnight
  var TRANSIT = { NZ: [1, 3], AU: [2, 7] };
  var TRANSIT_OTHER = [3, 10];
  // NZ public holidays as observed (Mondayised), plus Auckland Anniversary: no dispatch and no
  // NZ delivery. Matariki is fixed in law; the rest follow the holiday rules. Checked
  // 24/10/2026. Add the next year before this list runs out.
  var HOLIDAYS = [
    '2026-10-26', '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-01-04', '2027-02-01', '2027-02-08', '2027-03-26', '2027-03-29', '2027-04-26',
    '2027-06-07', '2027-06-25', '2027-10-25', '2027-12-27', '2027-12-28',
    '2028-01-03', '2028-01-04', '2028-01-31', '2028-02-07', '2028-04-14', '2028-04-17', '2028-04-25',
    '2028-06-05', '2028-07-14', '2028-10-23', '2028-12-25', '2028-12-26'
  ];
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAY_MS = 864e5;

  // Days are UTC-midnight Dates standing for NZ calendar days, so adding days never trips over
  // daylight saving.
  function nzNow() {
    var p = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false
    }).formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
    return { day: new Date(Date.UTC(+p.year, +p.month - 1, +p.day)), minutes: (+p.hour % 24) * 60 + +p.minute };
  }

  function iso(d) { return d.toISOString().slice(0, 10); }
  function addDays(d, n) { return new Date(d.getTime() + n * DAY_MS); }
  function isWorkday(d, nzHolidays) {
    var w = d.getUTCDay();
    return w !== 0 && w !== 6 && !(nzHolidays && HOLIDAYS.indexOf(iso(d)) !== -1);
  }
  function nextWorkday(d, nzHolidays) {
    do { d = addDays(d, 1); } while (!isWorkday(d, nzHolidays));
    return d;
  }
  function addWorkdays(d, n, nzHolidays) {
    for (var i = 0; i < n; i++) d = nextWorkday(d, nzHolidays);
    return d;
  }

  function day(d) { return DAYS[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()]; }
  function span(a, b) {
    return a.getUTCMonth() === b.getUTCMonth()
      ? DAYS[a.getUTCDay()] + ' ' + a.getUTCDate() + ' to ' + day(b)
      : day(a) + ' to ' + day(b);
  }
  function timeLeft(mins) {
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    return [h ? h + (h === 1 ? ' hr' : ' hrs') : '', m ? m + (m === 1 ? ' min' : ' mins') : ''].filter(Boolean).join(' ');
  }

  function estimate(country) {
    var now = nzNow();
    var sameDay = isWorkday(now.day, true) && now.minutes < CUTOFF;
    var dispatch = sameDay ? now.day : nextWorkday(now.day, true);
    var transit = TRANSIT[country] || TRANSIT_OTHER;
    var nz = country === 'NZ';
    var when;
    if (sameDay) when = 'Order in the next ' + timeLeft(CUTOFF - now.minutes) + ' for same-day dispatch';
    else if (iso(dispatch) === iso(addDays(now.day, 1))) when = 'Order now for dispatch tomorrow';
    else when = 'Order now for dispatch ' + day(dispatch);
    return { arrives: span(addWorkdays(dispatch, transit[0], nz), addWorkdays(dispatch, transit[1], nz)), when: when };
  }

  function paint(el) {
    var e = estimate(el.dataset.country || 'NZ');
    el.innerHTML = '<span class="wally-dx__line">Usually arrives <strong>' + e.arrives + '</strong></span>' +
      '<span class="wally-dx__sub">' + e.when + '</span>';
  }

  // The whole row (box icon + text) goes for sizes that don't ship now.
  function setShips(el, ships) {
    var row = el.closest('.group-block') || el;
    if (ships) row.removeAttribute('data-wally-dx-off');
    else row.setAttribute('data-wally-dx-off', '');
  }

  function init() {
    document.querySelectorAll('.wally-dx').forEach(function (el) {
      if (el.dataset.wallyDxReady) return;
      el.dataset.wallyDxReady = '1';
      setShips(el, el.dataset.ships !== 'false');
      paint(el);
    });
  }

  document.addEventListener('variant:update', function (event) {
    var target = event.target;
    if (!(target instanceof Element) || target.closest('product-card, dialog')) return;
    var section = target.closest('.shopify-section');
    var el = section && section.querySelector('.wally-dx');
    if (!el) return;
    var detail = event.detail || {};
    var variant = detail.resource;
    var html = detail.data && detail.data.html;
    setShips(el, !!(variant && variant.available) && !(html && html.querySelector('product-form-component .wally-preorder-note')));
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Keep the countdown honest while the page sits open.
  setInterval(function () { document.querySelectorAll('.wally-dx').forEach(paint); }, 30000);
})();
